import { Anchor, Quote, Shield, Star } from "lucide-react";

const CEO_PHOTO =
  "https://customer-assets.emergentagent.com/job_austin-yard-pro/artifacts/7imz4rkq_IMG_5695.jpeg";

export default function CEO() {
  return (
    <section
      id="meet-ceo"
      data-testid="ceo-section"
      className="relative py-24 md:py-32 bg-black overflow-hidden border-t border-border"
    >
      {/* Background flourishes */}
      <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container-ic relative">
        {/* Section overline */}
        <div className="overline mb-6" data-testid="ceo-overline">
          <Star className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
          Meet the CEO
        </div>

        {/* Bold italic tagline */}
        <h2
          className="font-display italic font-black uppercase tracking-tight leading-[0.9] text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-14 md:mb-20 max-w-5xl"
          data-testid="ceo-tagline"
        >
          Forged in Texas. <br />
          Built to <span className="text-primary">Serve.</span>{" "}
          <br className="hidden sm:block" />
          Ironclad to the Core.
        </h2>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          {/* Photo */}
          <div className="lg:col-span-6 relative order-2 lg:order-1">
            <div className="relative aspect-[4/5] w-full max-w-xl mx-auto lg:max-w-none">
              {/* Hairline frame */}
              <div className="absolute inset-0 border border-primary/30 -translate-x-3 -translate-y-3 pointer-events-none" />
              {/* Photo */}
              <div
                className="relative w-full h-full overflow-hidden bg-black"
                data-testid="ceo-photo-wrap"
              >
                <img
                  src={CEO_PHOTO}
                  alt="Christian Shanklin — Founder & CEO, Ironclad Yard Services"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  data-testid="ceo-photo"
                />
                {/* Vertical label */}
                <div className="absolute top-4 left-4 flex flex-col items-start">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-primary font-bold mb-2 [writing-mode:vertical-rl] rotate-180">
                    Christian Shanklin · Founder
                  </div>
                </div>
              </div>
              {/* Name plate */}
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-primary text-primary-foreground px-5 py-3 md:px-6 md:py-4 border border-black/20 shadow-xl">
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">
                  Founder · CEO
                </div>
                <div className="font-display text-xl md:text-2xl font-black uppercase tracking-tight leading-none mt-1">
                  Christian Shanklin
                </div>
              </div>
            </div>

            {/* Credentials strip */}
            <div className="mt-10 md:mt-14 grid grid-cols-3 border border-border divide-x divide-border">
              <div className="p-4 md:p-5 text-center">
                <Anchor className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  US Navy
                </div>
                <div className="font-display text-sm font-bold text-white mt-1">
                  Veteran
                </div>
              </div>
              <div className="p-4 md:p-5 text-center">
                <Shield className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Deep-Sea
                </div>
                <div className="font-display text-sm font-bold text-white mt-1">
                  Diver
                </div>
              </div>
              <div className="p-4 md:p-5 text-center">
                <Star className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Operations
                </div>
                <div className="font-display text-sm font-bold text-white mt-1">
                  Manager
                </div>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
            <p className="text-foreground/90 text-lg leading-relaxed">
              <span className="text-primary font-bold">Christian Shanklin</span>{" "}
              is a Navy veteran, deep-sea diver, and seasoned operations manager
              whose life has been shaped by grit, faith, and a relentless commitment
              to service. After years of navigating some of the toughest
              environments on earth, Christian founded Ironclad with a mission
              rooted in strength, integrity, and Texas pride.
            </p>

            {/* Vision Pull-quote */}
            <div className="relative border-l-2 border-primary pl-6 py-2 my-8">
              <Quote className="absolute -top-2 -left-3 h-6 w-6 text-primary bg-black p-1" />
              <div className="overline mb-2">His Vision</div>
              <p className="font-display italic text-white text-2xl md:text-3xl font-bold tracking-tight leading-snug">
                "Make every property as dependable and resilient as the people who
                call Texas home."
              </p>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Christian believes your outdoor space should be more than something
              you maintain — it should be a place where your family gathers, your
              friends feel welcome, and your best memories take shape. Whether
              you're refreshing your yard or preparing to host an unforgettable
              event, Christian brings the Ironclad Way to every project: precision,
              honesty, and a standard of excellence that never wavers.
            </p>

            {/* Closing tagline */}
            <div className="pt-4 mt-4 border-t border-border">
              <p className="font-display italic text-white text-base md:text-lg tracking-tight leading-snug">
                Rooted in <span className="text-primary">Texas.</span> Driven by{" "}
                <span className="text-primary">service.</span> Dedicated to
                restoring beauty and building strength{" "}
                <span className="text-primary">— one property at a time.</span>
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-6">
              <a
                href="#booking"
                className="btn-primary"
                data-testid="ceo-cta-book"
              >
                Work With Christian
              </a>
              <a
                href="tel:5129480676"
                className="btn-outline"
                data-testid="ceo-cta-call"
              >
                Call 512-948-0676
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
